import type { ChangeRequest, ModerationEntity } from '../model/schema';
import {
  entityNumber,
  entityString,
  entityText,
  payloadBag,
  payloadEntityOf,
  requestBaselineOf,
} from './entity-bag';
import { formatMoney } from './format';
import { requestParentId, requestParentName } from './names';
import { prettifyEnumLabel } from './status-labels';
import { firstNonEmpty } from './translate';

/** Table-level fields derived from a request's proposed `payload`. */

/** Product SKU from the proposed data (`sku` / `articul` / `vendor_code`…). */
export function requestSku(request: ChangeRequest): string | undefined {
  const entity = payloadEntityOf(request);
  for (const key of ['sku', 'articul', 'article', 'vendor_code', 'barcode', 'code']) {
    const value = entityString(entity, key);
    if (value !== '') return value;
  }
  return undefined;
}

/**
 * A dish's category name: the row's `parent` (a product's parent IS its
 * category, filled on the list reads), else the proposed / previous data.
 */
export function requestCategoryName(request: ChangeRequest): string {
  const parentName = requestParentName(request, '');
  if (parentName !== '') return parentName;
  const entity = payloadEntityOf(request);
  const baseline = requestBaselineOf(request) ?? {};
  for (const bag of [entity, baseline]) {
    for (const key of ['product_category_name', 'category_name', 'category']) {
      const value = entityText(bag, key, '');
      if (value !== '') return value;
    }
  }
  return '—';
}

/**
 * A promotion's branch id: the row's `parent` (a promotion's parent is its
 * branch), else `restaurant_id` on the request or its data. `''` for a
 * network-wide promotion.
 */
export function requestBranchId(request: ChangeRequest): string {
  return (
    firstNonEmpty(
      requestParentId(request, 'restaurant'),
      entityString(payloadEntityOf(request), 'restaurant_id'),
      entityString(requestBaselineOf(request) ?? {}, 'restaurant_id'),
      request.restaurant_id?.trim(),
    ) ?? ''
  );
}

/** Effective price from the proposed data (override-aware, like the table). */
export function requestPrice(request: ChangeRequest): number | undefined {
  const entity = payloadEntityOf(request);
  return (
    entityNumber(entity, 'effective_price') ?? entityNumber(entity, 'price') ?? undefined
  );
}

/**
 * Promotion terms for the queue table: the pending `payload` wins per
 * field, the `baseline` fills the gaps. Transition payloads are sparse
 * (`{lifecycle_status}`), so reading the payload alone renders Тип/Скидка
 * as `—` even though the promotion carries terms.
 */
export function requestPromotionTerms(request: ChangeRequest): ModerationEntity {
  return { ...(requestBaselineOf(request) ?? {}), ...payloadBag(request) };
}

/** Promotion kind for the Тип column (`Discount`, `Buy X Get Y`). */
export function requestPromoType(request: ChangeRequest): string {
  const entity = requestPromotionTerms(request);
  const raw = entityString(entity, 'promo_type').toUpperCase();
  if (raw !== '' && !raw.includes('UNSPECIFIED')) {
    if (raw.includes('BUY')) return 'Buy X Get Y';
    if (raw.includes('DISCOUNT')) return 'Discount';
    return prettifyEnumLabel(raw);
  }
  // Real payloads often omit `promo_type` — derive it from the terms.
  const buy = entityNumber(entity, 'buy_quantity');
  const get = entityNumber(entity, 'get_quantity');
  if (buy !== undefined || get !== undefined) return 'Buy X Get Y';
  if (entityString(entity, 'discount_type') !== '') return 'Discount';
  if (entityNumber(entity, 'discount_value') !== undefined) return 'Discount';
  return '—';
}

/** Promotion discount line (`20%`, `25 000 сум`, `2 → 1`) for dense cells. */
export function requestDiscount(request: ChangeRequest): string {
  const entity = requestPromotionTerms(request);
  const discountType = entityString(entity, 'discount_type').toUpperCase();
  const value = entityNumber(entity, 'discount_value');
  if (value !== undefined) {
    return discountType.includes('PERCENT') ? `${value}%` : formatMoney(value);
  }
  const buy = entityNumber(entity, 'buy_quantity');
  const get = entityNumber(entity, 'get_quantity');
  if (buy !== undefined || get !== undefined) {
    return `${buy ?? '—'} → ${get ?? '—'}`;
  }
  return '—';
}
